'use client';
// ═══════════════════════════════════════════════════════════════════════════
// BOUNDARY DE SEGMENT + AUTO-RÉPARATION (incident 24/08/2026).
//
// Avant ce fichier, le SEUL boundary était global-error.js : n'importe quel
// crash sous app/page.js (donc tout le dashboard) démontait la racine entière,
// et « Réessayer » rejouait le même crash — l'app est restée inutilisable
// ~40 min jusqu'à un fix mergé en urgence (#2295).
//
// Ce boundary attrape les crashs du segment SANS toucher <html> et tente UNE
// réparation automatique avant de déranger l'utilisateur : l'app rouvre au
// boot la dernière page visitée (aureus_current_page) — si CETTE page crashe,
// l'utilisateur est enfermé dans une boucle. On purge donc la page restaurée,
// retour au dashboard, et on relance tout seul. Anti-boucle : une seule
// tentative par minute (sessionStorage) ; si ça re-crashe, écran manuel.
//
// Chaque passage est signalé à /api/error-logs (compté par le détecteur de
// panne totale → alerte Telegram au 3e crash en 15 min) en plus de Sentry.
// ═══════════════════════════════════════════════════════════════════════════
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

const CLE_ANTI_BOUCLE = 'aureus_auto_repare_le';

export default function Error({ error, reset }) {
  const [autoReparation, setAutoReparation] = useState(false);

  useEffect(() => {
    Sentry.captureException(error);
    try {
      fetch('/api/error-logs', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: 'fatal',
          source: 'app-error-boundary',
          message: String(error?.message || error || 'Erreur inconnue').slice(0, 1500),
          stack: String(error?.stack || '').slice(0, 1800),
          url: typeof window !== 'undefined' ? window.location.href : '',
        }),
      }).catch(() => {});
    } catch (_) { /* le signalement ne re-crashe jamais */ }
  }, [error]);

  // Auto-réparation : purger la page restaurée et relancer — une fois par minute.
  useEffect(() => {
    try {
      const derniere = parseInt(sessionStorage.getItem(CLE_ANTI_BOUCLE) || '0', 10);
      if (Date.now() - derniere < 60000) return; // déjà tenté → écran manuel
      sessionStorage.setItem(CLE_ANTI_BOUCLE, String(Date.now()));
      localStorage.removeItem('aureus_current_page'); // la page qui crashe ne se rouvre plus
      setAutoReparation(true);
      const t = setTimeout(() => { try { reset(); } catch (_) { window.location.reload(); } }, 800);
      return () => clearTimeout(t);
    } catch (_) { /* stockage indisponible → écran manuel */ }
  }, [error, reset]);

  if (autoReparation) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0e1a', color: '#e8eaed', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>🔧</div>
          <div style={{ fontSize: 15, opacity: 0.85 }}>Réparation automatique en cours…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0e1a', color: '#e8eaed', fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 480 }}>
        <h1 style={{ fontSize: 22, marginBottom: 12, color: '#d4af37' }}>Une erreur est survenue</h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, opacity: 0.8, marginBottom: 24 }}>
          La réparation automatique n&apos;a pas suffi — l&apos;incident est enregistré et
          l&apos;équipe est alertée. Vous pouvez réessayer manuellement.
        </p>
        <button
          onClick={() => { try { localStorage.removeItem('aureus_current_page'); } catch (_) {} reset(); }}
          style={{ background: '#d4af37', color: '#0a0e1a', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
        >
          Réessayer
        </button>
      </div>
    </div>
  );
}
